<template>
    <v-select
        ref="selectRef"
        v-model="displayValue"
        v-bind="safeFieldProps"
        :label="computedLabel"
        variant="outlined"
        :items="props.items"
        :item-title="itemTitle"
        :item-value="itemValue"
        :multiple="props.multiple"
        :chips="chips"
        :closable-chips="props.closableChips"
        :prepend-inner-icon="props.prependInnerIcon"
        :hide-details="!!props.hideDetails"
        :append-inner-icon="props.appendInnerIcon"
        :return-object="props.returnObject"
        :rules="rules"
        :disabled="props.disabled"
        :readonly="props.readonly"
        :loading="props.loading"
        @update:menu="onMenuUpdate"
        rounded
        :color="props.color"
        :base-color="props.baseColor"
        :required="props.required"
        :clearable="clearableValue"
        :menu-props="props.menuProps"
        autocomplete="off"
    >
        <!-- Slot de sélection personnalisé (activé uniquement si chips est true) -->
        <template v-if="props.chips" #selection="{ item, index }">
            <v-chip
                v-if="hasTitle(item)"
                closable
                size="small"
                class="ma-1"
                @click:close="removeItem(index)"
            >
                {{ getItemTitle(item) }}
            </v-chip>
        </template>

        <!-- Slots personnalisés si besoiin -->
        <template v-for="(_, slot) in $slots" v-slot:[slot]="scope">
            <slot :name="slot" v-bind="scope || {}" />
        </template>
    </v-select>
</template>

<script setup lang="ts">
    import { computed, ref, nextTick, onBeforeUnmount } from 'vue';
    import type { SelectInstance } from '@/interfaces/common';

    type SelectValue = unknown;

    const props = withDefaults(
        defineProps<{
            modelValue: SelectValue;
            fieldProps?: unknown;
            label?: string;
            items: unknown[];
            itemTitle?: string | ((item: unknown) => string);
            itemValue?: string | ((item: unknown) => string | number);
            multiple?: boolean;
            chips?: boolean;
            prependInnerIcon?: string;
            appendInnerIcon?: string;
            returnObject?: boolean;
            rules?: ((value: unknown) => boolean | string)[];
            disabled?: boolean;
            readonly?: boolean;
            required?: boolean;
            enableInfinite?: boolean;
            color?: string;
            baseColor?: string;
            hideDetails?: boolean;
            clearable?: boolean;
            closableChips?: boolean;
            menuProps?: Record<string, unknown>;
            loading?: boolean;
        }>(),
        { clearable: false },
    );

    const emit = defineEmits<{
        (e: 'update:modelValue', value: SelectValue): void;
        (e: 'load-more'): void;
    }>();

    const handleValueChange = (val: SelectValue | undefined) => {
        if (props.multiple) {
            emit('update:modelValue', Array.isArray(val) ? val : []);
        } else {
            emit('update:modelValue', val === undefined ? null : val);
        }
    };

    const chips = computed(() => {
        // if chips is explicitly set, use it; otherwise default to multiple mode (for better UX)
        return props.chips !== undefined ? props.chips : !!props.multiple;
    });

    const innerValue = computed({
        get: () => {
            if (props.multiple) {
                return Array.isArray(props.modelValue) ? props.modelValue : [];
            }
            return props.modelValue === undefined || props.modelValue === null
                ? null
                : props.modelValue;
        },
        set: handleValueChange,
    });

    const displayValue = computed({
        get: () => {
            if (!props.items.length) {
                return props.multiple ? [] : null;
            }
            return innerValue.value;
        },
        set: handleValueChange,
    });

    const clearableValue = computed(() => {
        if (props.clearable) {
            return Array.isArray(props.modelValue)
                ? props.modelValue.length > 0
                : !!props.modelValue;
        }
        return props.clearable;
    });

    const safeFieldProps = computed(() => props.fieldProps ?? {});
    const computedLabel = computed(() => {
        if (props.label) {
            return props.required ? `${props.label} *` : props.label;
        }
        return undefined;
    });
    const itemTitle = computed(() => props.itemTitle ?? 'title');
    const itemValue = computed(() => props.itemValue ?? 'value');
    const rules = computed(() => props.rules ?? []);
    const selectRef = ref<SelectInstance | null>(null);

    type NormalizedItem = { title?: unknown; value?: unknown } | undefined;
    const getItemTitle = (it: NormalizedItem): string => {
        const t =
            it && typeof (it as { title?: unknown }).title !== 'undefined'
                ? (it as { title?: unknown }).title
                : '';
        return (t == null ? '' : String(t)).trim();
    };
    const hasTitle = (it: NormalizedItem): boolean => getItemTitle(it).length > 0;

    const removeItem = (index: number) => {
        if (props.multiple) {
            const current = Array.isArray(props.modelValue) ? props.modelValue : [];
            const next = current.filter((_, i: number) => i !== index);
            emit('update:modelValue', next as SelectValue);
        } else {
            emit('update:modelValue', null as SelectValue);
        }
    };

    /* --- ton code d’infinite scroll reste inchangé --- */
    let menuContentEl: HTMLElement | null = null;
    const handleScroll = () => {
        if (!props.enableInfinite || !menuContentEl) return;
        const threshold = 50;
        if (
            menuContentEl.scrollTop + menuContentEl.clientHeight >=
            menuContentEl.scrollHeight - threshold
        ) {
            emit('load-more');
        }
    };

    const findOverlayContent = (): HTMLElement | null => {
        const overlays = document.querySelectorAll<HTMLElement>(
            '.v-overlay__content, .v-overlay-content',
        );
        return overlays.length ? overlays[overlays.length - 1] : null;
    };

    const getScrollableElement = (overlay: HTMLElement): HTMLElement => {
        const inner = overlay.querySelector<HTMLElement>(
            '.v-virtual-scroll__content, .v-virtual-scroll, .v-list, [role="listbox"]',
        );
        return inner ?? overlay;
    };

    const attachScrollListener = () => {
        if (!props.enableInfinite) return;
        nextTick(() => {
            const overlay = findOverlayContent();
            if (!overlay) return;
            const scrollEl = getScrollableElement(overlay);
            scrollEl.addEventListener('scroll', handleScroll);
            menuContentEl = scrollEl;
        });
    };

    const detachScrollListener = () => {
        if (menuContentEl) {
            menuContentEl.removeEventListener('scroll', handleScroll);
            menuContentEl = null;
        }
    };

    const onMenuUpdate = (isOpen: boolean) => {
        if (isOpen) attachScrollListener();
        else detachScrollListener();
    };

    onBeforeUnmount(() => {
        detachScrollListener();
    });
</script>
